import React from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { render } from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import createHistory from 'history/createBrowserHistory';
import { CometTheme } from '@zillow/zrs-comet';
import { IconFont } from '@zillow/zrs-zsg-icon';
import * as Sentry from '@sentry/browser';
import { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';

// Fetch polyfill for older browsers
import 'whatwg-fetch';
import smoothscroll from 'smoothscroll-polyfill';
import 'url-search-params-polyfill';

// Multibrand-UI Components
import ThemeProvider from '@zg-rentals/multibrand-ui/lib/ThemeProvider';

// zrm-utils
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';

import './utils/log';

import App from 'app/shared/containers/App';
import configureStore from 'app/shared/configureStore';
import pathsManifest from 'app/shared/pathsManifest';

// analytics
import MultiAnalyticsModule from 'app/shared/MultiAnalyticsModule';

// rmw provider
import RMWProvider from 'app/shared/providers/RMWProvider';

// utils
import urlUtils from 'app/shared/utils/urlUtils';

// selectors
import AppSelectors from 'app/shared/selectors/AppSelectors';

if (__SENTRY_DSN__) {
    Sentry.init({
        dsn: __SENTRY_DSN__,
        whitelistUrls: [
            /https?:\/\/((nodes3cdn)\.)?hotpads\.com/,
        ],
    });
}

window.browserHistory = createHistory();
// eslint-disable-next-line no-underscore-dangle
window.store = configureStore(window.__INITIAL_STATE__, window.browserHistory);

smoothscroll.polyfill();

// browserHistory.listen((location) => {
//     //prevent history pushes of identical path
//     if (window.store.getState().routing.location.pathname !== location.pathname) {
//         push(location);
//         window.scroll(0,0);
//     } else {
//         replace(location);
//     }
// });

/*
 * Event before route changes
 *
 * @param {obj} location - The location to be transferred to
 * @param {string} action - The action is one of PUSH, REPLACE, or POP
 *     depending on how the user got to the current URL.
 * @returns {bool}
 */
window.browserHistory.block((location, action) => {
    const { pathname: targetPath, search: targetSearchQueryParams, state = {} } = location;

    if (action === 'PUSH' && state.persistQueryParams !== true) {
        const currentSearchQueryParams = window.document.location.search;

        // add target search to whitelist
        const queryParamObj = ZRMUtils.queryUtils.parse(targetSearchQueryParams);
        const tempWhiteList = Object.keys(queryParamObj);

        // rebuild full path
        const fullPath = `${targetPath}${currentSearchQueryParams}${targetSearchQueryParams}`;

        // set path with persisted query params
        const targetNavPath = ZRMUtils.queryUtils.getPathWithPresistedQueryParams(fullPath, tempWhiteList);

        // timeout is required otherwise query param is not displayed in url
        setTimeout(() => {
            window.browserHistory.replace(targetNavPath, {
                persistQueryParams: true,
            });
        }, 0);
    }

    return true;
});

window.router = {
    getPath(t) {
        let tempPath = '';

        if (typeof t === 'string') {
            tempPath = t;
        } else if (typeof t === 'object') {
            const { pathname = '', search = '' } = t;
            tempPath = `${pathname}${search}`;
        }

        const locationObj = ZRMUtils.queryUtils.parsePathAndQueryParams(tempPath);
        const { queryParams } = locationObj;

        const queryParamObj = ZRMUtils.queryUtils.parse(queryParams);
        const tempWhiteList = Object.keys(queryParamObj);
        const fullPath = `${tempPath}`;

        return ZRMUtils.queryUtils.getPathWithPresistedQueryParams(fullPath, tempWhiteList);
    },

    updateLocation(path, target = '_self') {
        window.open(path, target);
    },

    redirectTo(t) {
        const path = this.getPath(t);
        return window.browserHistory.replace(path);
    },
    transitionTo(t) {
        const path = this.getPath(t);

        window.browserHistory.push(path);
        window.scroll(0, 0);
    },
};

const setupRentalManagerApiWebSocket = (webSocketHost) => {
    window.rentalManagerApiWebSocket = new WebSocketWrapper({
        url: `wss://${webSocketHost}/rental-manager-api/ws/services`,

        // wait up to 5 minutes before trying to reconnect again
        // resets on successfully conn
        // wait time doubles every failed conn attempt
        maxReconnect: 60000 * 5,
    });
};

const renderApp = () => {
    const state = window.store.getState();
    const { brand, cookieEnabledServices, isRunningInProd } = state.app;
    const { hostApp } = cookieEnabledServices;
    const paths = pathsManifest.getAllPaths(brand);
    const isInMobileWebView = (hostApp === 'android') || (hostApp === 'ios');
    const hostName = AppSelectors.makeHostname(state);
    const webSocketHost = urlUtils.removeProtocol(hostName);

    setupRentalManagerApiWebSocket(webSocketHost);

    // init multi analytics above render
    new MultiAnalyticsModule().init({
        brand,
        isProd: isRunningInProd,
        drymode: __DEV__,
    }).getMa();

    render(
        <Provider store={window.store}>
            <ThemeProvider brand={brand}>
                <RMWProvider
                    brand={brand}
                    isInMobileWebView={isInMobileWebView}
                    paths={paths}
                >
                    <IconFont />
                    <SCThemeProvider theme={CometTheme}>
                        <ConnectedRouter history={window.browserHistory}>
                            <App />
                        </ConnectedRouter>
                    </SCThemeProvider>
                </RMWProvider>
            </ThemeProvider>
        </Provider>,
        document.getElementById('app-root'),
    );
};

renderApp();
